import { r as registerInstance, c as createEvent, h, H as Host, g as getElement } from './index-bb0e68fd.js';
import { d as debounce, t as throttle } from './index-d3f1363d.js';
import { g as getUserLang, p as persistLanguageChoice, t as translate } from './language.service-490983de.js';
import { u as uniqueId } from './utils-c8805f81.js';
import { s as state, d as dispose } from './store-b1332c1e.js';
import { S as SvgSprite } from './svg-sprite.component-abb69a1b.js';
import { S as SvgIcon } from './svg-icon.component-70692acc.js';
import { I as If } from './if.component-d88d9e48.js';

/**
 * Activate the current route based on the config and the match mode
 * @param config Main Navigation Config
 * @param activeRouteProp Match mode or custom URL to compare against
 * @returns Modified Main Navigation Config
 */
const markActiveRoute = (config, activeRouteProp) => {
    // Don't highlight any route
    if (activeRouteProp === false) {
        return config;
    }
    // Set reference URL to compare nav items against
    let referenceUrl;
    let isRelativeReference = false;
    config = resetOverrideConfig(config);
    if (activeRouteProp === 'auto' || activeRouteProp === 'exact') {
        // Check if an active route is already configured, set override to that and return
        if (hasActivePortalRoute(config)) {
            return resetActiveStateToPortalConfig(config);
        }
        referenceUrl = new URL(window.location.href);
    }
    else {
        try {
            isRelativeReference = activeRouteProp.startsWith('/') || activeRouteProp.startsWith('./');
            referenceUrl = new URL(activeRouteProp, window.location.href);
        }
        catch (error) {
            console.warn(`Active Route: ${activeRouteProp} is not a valid URL. Navigation highlighting has been disabled.`);
            return config;
        }
    }
    const scoreList = compileScoreList(config, referenceUrl, activeRouteProp, isRelativeReference);
    if (scoreList.length === 0) {
        // No match found or already active links defined
        return config;
    }
    const winnerPair = scoreList[0];
    winnerPair.main.isActiveOverride = true;
    if (winnerPair.sub)
        winnerPair.sub.isActiveOverride = true;
    return config;
};
/**
 * Check if the portal config set any active route
 * @param config Main navigation config
 * @returns True if portal set any route as active
 */
const hasActivePortalRoute = (config) => {
    return config.filter(nav => nav.isActive).length > 0;
};
const resetActiveStateToPortalConfig = (config) => {
    return config.map(nav => {
        var _a;
        return (Object.assign(Object.assign({}, nav), { isActiveOverride: nav.isActive, flyout: (_a = nav.flyout) === null || _a === void 0 ? void 0 : _a.map(flyout => (Object.assign(Object.assign({}, flyout), { linkList: flyout.linkList.map(link => (Object.assign(Object.assign({}, link), { isActiveOverride: link.isActive }))) }))) }));
    });
};
const resetOverrideConfig = (config) => {
    return config.map(nav => {
        var _a;
        return (Object.assign(Object.assign({}, nav), { isActiveOverride: false, 
            // Initialize flyout as an empty array if it's undefined
            flyout: ((_a = nav.flyout) === null || _a === void 0 ? void 0 : _a.map(flyout => (Object.assign(Object.assign({}, flyout), { linkList: flyout.linkList.map(link => (Object.assign(Object.assign({}, link), { isActiveOverride: false }))) })))) || [] }));
    });
};
/**
 * Compile a list of scores based on how well nav items match the reference URL
 * @param config Main Nav Config
 * @param referenceUrl URL to compare nav items against (window.location or custom)
 * @param activeRouteProp Match mode
 * @returns A list of scored URLs if any matched
 */
const compileScoreList = (config, referenceUrl, activeRouteProp, isRelativeReference) => {
    // Flag to check if the Portal set any active links or if there are any exact matches
    let hadAnyActiveLink = false;
    const scoreList = [];
    config
        .filter(mainNav => !hadAnyActiveLink && mainNav)
        .forEach(mainNav => {
        var _a;
        try {
            const navItemUrl = new URL(mainNav.url);
            const score = compareRoutes(referenceUrl, navItemUrl, isRelativeReference, activeRouteProp);
            hadAnyActiveLink = updateScoreList(scoreList, score, { main: mainNav, score }, hadAnyActiveLink);
        }
        catch (_b) {
            // Not a valid url, continue
        }
        // Loop through flyout links 2nd level
        (_a = mainNav.flyout) === null || _a === void 0 ? void 0 : _a.forEach(flyout => {
            var _a;
            (_a = flyout.linkList) === null || _a === void 0 ? void 0 : _a.forEach(linklist => {
                // Don't override if any link is already active
                if (linklist.isActive && (activeRouteProp === 'auto' || activeRouteProp === 'exact')) {
                    hadAnyActiveLink = true;
                    return;
                }
                try {
                    const flyoutNavUrl = new URL(linklist.url);
                    const score = compareRoutes(referenceUrl, flyoutNavUrl, isRelativeReference, activeRouteProp);
                    hadAnyActiveLink = updateScoreList(scoreList, score, { main: mainNav, sub: linklist, score }, hadAnyActiveLink);
                }
                catch (_a) {
                    // Not a valid URL, continue
                }
            });
        });
    });
    return scoreList.sort((a, b) => b.score - a.score);
};
/**
 * Update score list
 */
function updateScoreList(scoreList, score, scoreListItem, hadAnyActiveLink) {
    let hadActiveLink = hadAnyActiveLink;
    if (score > 0) {
        if (score === Infinity)
            hadActiveLink = true;
        // Push score
        scoreList.push(scoreListItem);
    }
    return hadActiveLink;
}
/**
 * Compare a nav item URL against the reference URL for similarity
 * @param referenceUrl URL we're comparing against (from window.location or custom)
 * @param navItemUrl URL from the navigation item
 * @param matchMode exact or auto matching
 * @returns Score indicating how well the URLs match
 */
const compareRoutes = (referenceUrl, navItemUrl, isRelativeReference, matchMode) => {
    if (referenceUrl === null || navItemUrl === null) {
        return 0;
    }
    // Only compare origins if reference URL is not relative
    if (!isRelativeReference && referenceUrl.origin !== navItemUrl.origin) {
        return 0;
    }
    const referenceUrlPath = referenceUrl.pathname.toLocaleLowerCase();
    const navItemUrlPath = navItemUrl.pathname.toLocaleLowerCase();
    // Exact match, pathname is the same
    if (referenceUrlPath === navItemUrlPath) {
        return Infinity;
    }
    // The reference path is shorter than nav item path, a match is impossible
    if (referenceUrl.pathname.length < navItemUrl.pathname.length) {
        return 0;
    }
    if (matchMode === 'auto') {
        const referenceSegments = [
            referenceUrl.origin.toLocaleLowerCase(),
            ...referenceUrlPath.split('/').filter(x => x !== null && x !== ''),
        ];
        const navItemSegments = [
            navItemUrl.origin.toLocaleLowerCase(),
            ...navItemUrlPath.split('/').filter(x => x !== null && x !== ''),
        ];
        const score = getSimilarityScore(referenceSegments, navItemSegments);
        return Math.min(referenceSegments.length, navItemSegments.length) === score ? score : 0;
    }
    return 0;
};
/**
 * Check how many items in an array match
 * @param a Reference array
 * @param b Nav item array to compare
 * @returns Score
 */
const getSimilarityScore = (a, b) => {
    if (a == null || b == null || a.length === 0 || b.length === 0) {
        return 0;
    }
    let i = 0;
    for (; i < Math.min(a.length, b.length); i++) {
        if (a[i] !== b[i]) {
            return 0;
        }
    }
    return i;
};

// Prevent double requests
let request = null;
// Cache the original os flyout to use it for updates
let osFlyoutCache = null;
const getPPMConfig = () => {
    const ppmConfigScript = document.querySelector('#PPM_HEADER_DATA');
    if ((ppmConfigScript === null || ppmConfigScript === void 0 ? void 0 : ppmConfigScript.textContent) != null) {
        return JSON.parse(ppmConfigScript.textContent);
    }
    return null;
};
/**
 * Get a localized config object
 *
 * @param projectId String identifying the project
 * @param language [optional] Preferred language
 * @returns Localized config object
 */
const getLocalizedConfig = async ({ projectId, environment, language, cookieKey, localStorageKey, activeRouteProp, localizedCustomConfig, osFlyoutOverrides, }) => {
    var _a, _b;
    const ppmConfig = getPPMConfig();
    let localizedConfig;
    let config = null;
    let lang;
    if (ppmConfig) {
        localizedConfig = ppmConfig;
        lang = document.documentElement.lang;
    }
    else {
        if (request == null) {
            request = fetchConfig(projectId, environment);
        }
        config = await request;
        lang = getUserLang(Object.keys(config), language, localStorageKey, cookieKey);
        if (lang === undefined) {
            throw new Error('Internet Header: unable to determine current language');
        }
        // Clone config for more predictable state updates
        localizedConfig = Object.assign({}, config[lang]);
    }
    // Merge custom config with portal config
    if (localizedCustomConfig) {
        const header = ((_a = localizedCustomConfig === null || localizedCustomConfig === void 0 ? void 0 : localizedCustomConfig.header) === null || _a === void 0 ? void 0 : _a.navMain)
            ? Object.assign(Object.assign({}, localizedConfig.header), { navMain: [...localizedConfig.header.navMain, ...localizedCustomConfig.header.navMain] }) : localizedConfig.header;
        localizedConfig = Object.assign(Object.assign({}, localizedConfig), { header });
    }
    // Add entries to flyout_os
    if (osFlyoutOverrides)
        localizedConfig.header.navMain = mergeOsFlyoutOverrides(localizedConfig, osFlyoutOverrides);
    // Mark active route
    if (activeRouteProp != null)
        localizedConfig.header.navMain = markActiveRoute(localizedConfig.header.navMain, activeRouteProp);
    setMainNavigationIds((_b = localizedConfig === null || localizedConfig === void 0 ? void 0 : localizedConfig.header) === null || _b === void 0 ? void 0 : _b.navMain);
    // Set the new language choice
    persistLanguageChoice(lang, cookieKey, localStorageKey);
    return localizedConfig;
};
/**
 * Merge portal config with custom os flyout overrides
 * @param config Localized config
 * @param osFlyoutOverrides Overrides for the flyout
 * @returns Merged localized config
 */
const mergeOsFlyoutOverrides = (config, osFlyoutOverrides) => {
    if (osFlyoutOverrides == null)
        return config.header.navMain;
    return config.header.navMain.map(mainNav => {
        var _a, _b, _c;
        if (mainNav.id !== 'flyout_os')
            return mainNav;
        if (osFlyoutCache === null) {
            osFlyoutCache = JSON.parse(JSON.stringify(mainNav));
        }
        const mainNavText = (_a = osFlyoutOverrides.text) !== null && _a !== void 0 ? _a : osFlyoutCache.text;
        const mainNavTitle = (_b = osFlyoutOverrides.title) !== null && _b !== void 0 ? _b : osFlyoutCache.title;
        if (osFlyoutCache.flyout == null ||
            osFlyoutCache.flyout.length === 0 ||
            osFlyoutOverrides.flyout == null ||
            osFlyoutOverrides.flyout.length === 0)
            return Object.assign(Object.assign({}, mainNav), { title: mainNavTitle, text: mainNavText });
        // Add entries for os flyout columns without overriding existing config
        const mainNavFlyout = [
            ...osFlyoutCache.flyout.map((col, index) => {
                var _a, _b;
                const overrides = (_a = osFlyoutOverrides.flyout) === null || _a === void 0 ? void 0 : _a[index]; // Check if overrides exist
                const title = (_b = overrides === null || overrides === void 0 ? void 0 : overrides.title) !== null && _b !== void 0 ? _b : col.title;
                // Check if the linkList exists in the overrides and then use it
                const linkList = (overrides === null || overrides === void 0 ? void 0 : overrides.linkList) != null ? overrides.linkList : [];
                return {
                    title,
                    linkList: [...col.linkList, ...linkList],
                };
            }),
            ...(((_c = osFlyoutOverrides.flyout) === null || _c === void 0 ? void 0 : _c.slice(osFlyoutCache.flyout.length)) || []),
        ];
        return Object.assign(Object.assign({}, mainNav), { text: mainNavText, title: mainNavTitle, flyout: mainNavFlyout });
    });
};
/**
 * Fetch the general config based on project id
 *
 * @returns Promise for Post Portal general config
 */
const fetchConfig = async (projectId, environment) => {
    // Check if project id is sanitized
    if (!isValidProjectId(projectId)) {
        throw new Error(`Internet Header: invalid project id "${projectId}"`);
    }
    if (projectId === 'test') {
        environment = 'int01';
    }
    let url = generateConfigUrl(projectId, environment);
    // Get the config if cache is invalid
    try {
        const res = await fetch(url);
        return (await res.json());
    }
    catch (error) {
        throw new Error(`Internet Header: fetching config failed. ${error.message}`);
    }
};
/**
 * Generate an URL with all necessary query params to get the configuration.
 * Project id "test" will return a test configuration
 * @param projectId string
 * @param environment int01, int02 or prod
 * @returns URL pointing to the project config
 */
const generateConfigUrl = (projectId, environment) => {
    if (projectId === 'test')
        return 'assets/config/test-configuration.json';
    const parsedEnvironment = environment.toLowerCase();
    const isProd = parsedEnvironment === 'prod';
    const host = `https://${isProd ? 'www' : 'int'}.post.ch`;
    try {
        // Use URL to validate the generated URL
        return new URL(`${host}/api/headerjs/Json?serviceid=${encodeURIComponent(projectId)}${!isProd ? '&environment=' + parsedEnvironment : ''}`).toString();
    }
    catch (error) {
        throw new Error(`Internet Header: Config URL is invalid.`);
    }
};
/**
 * Check if project id contains only URL safe characters
 *
 * @param projectId Project Id string
 * @returns The valid project id
 */
const isValidProjectId = (projectId) => {
    return projectId !== '' && projectId.length > 0 && /^[a-zA-Z][\w-]*[a-zA-Z0-9]$/.test(projectId);
};
/**
 * Get the localized config object from a custom config
 *
 * @param config String or json object of the custom config
 * @param language Specific language
 * @returns Localized custom config object
 */
const getLocalizedCustomConfig = (config, language) => {
    var _a;
    let customConfig;
    try {
        customConfig = typeof config === 'string' ? JSON.parse(config) : config;
    }
    catch (error) {
        throw new Error(`Internet Header: Custom config is invalid. Make sure your custom config contains valid JSON syntax and matches the definition. `);
    }
    let localizedCustomConfig = customConfig[language];
    if ((_a = localizedCustomConfig === null || localizedCustomConfig === void 0 ? void 0 : localizedCustomConfig.header) === null || _a === void 0 ? void 0 : _a.navMain)
        setMainNavigationIds(localizedCustomConfig.header.navMain);
    return localizedCustomConfig;
};
/**
 * Set unique ID's on main navigation entities but conserve flyout_os to be able to
 * identify the online-service flyout, which can be configured by osFlyoutOverrides
 */
const setMainNavigationIds = (navMainEntities) => {
    navMainEntities.forEach(navMainEntity => {
        navMainEntity.id = navMainEntity.id === 'flyout_os' ? navMainEntity.id : uniqueId('main-nav-');
    });
};

const name = "@swisspost/internet-header";
const version = "1.15.16";
const description = "The header for client facing applications.";
const author = "Swiss Post <design-system@post.ch>";
const license = "Apache-2.0";
const repository = {
	type: "git",
	url: "https://github.com/swisspost/design-system.git"
};
const homepage = "https://design-system.post.ch";
const bugs = {
	url: "https://github.com/swisspost/design-system/issues"
};
const main = "loader/index.cjs.js";
const module = "loader/index.js";
const es2017 = "loader/index.es2017.js";
const types = "loader/index.d.ts";
const collection = "dist/collection/collection-manifest.json";
const unpkg = "dist/swisspost-internet-header/swisspost-internet-header.esm.js";
const publishConfig = {
	access: "public"
};
const files = [
	"dist/",
	"loader/"
];
const scripts = {
	dev: "stencil build --serve --port 9310 --watch --docs-readme --dev --config stencil.config.dev.ts",
	start: "stencil build --watch --docs-readme",
	build: "stencil build --docs-readme",
	clean: "rimraf www dist loader",
	test: "pnpm run unit",
	unit: "jest",
	"unit:watch": "jest --watch",
	e2e: "cypress run",
	"e2e:ci": "xvfb-run -a cypress run",
	"e2e:watch": "cypress open",
	lint: "eslint src/**/*{.ts,.tsx}",
	"lint:fix": "eslint src/**/*{.ts,.tsx} --fix",
	generate: "stencil generate"
};
const dependencies = {
	"@swisspost/design-system-styles": "workspace:9.0.2",
	"body-scroll-lock": "4.0.0-beta.0",
	"iframe-resizer": "4.4.5",
	jquery: "3.7.1",
	"throttle-debounce": "5.0.2",
	"url-polyfill": "1.1.12"
};
const devDependencies = {
	"@babel/core": "7.25.2",
	"@percy/cli": "1.29.1",
	"@percy/cypress": "3.1.2",
	"@stencil-community/eslint-plugin": "0.8.0",
	"@stencil/core": "4.19.2",
	"@stencil/sass": "3.0.12",
	"@stencil/store": "2.0.16",
	"@types/body-scroll-lock": "3.1.2",
	"@types/iframe-resizer": "3.5.13",
	"@types/jest": "29.5.12",
	"@types/jquery": "3.5.30",
	"@types/node": "20.14.14",
	"@types/throttle-debounce": "5.0.2",
	"babel-loader": "9.1.3",
	bootstrap: "5.3.3",
	cypress: "13.13.2",
	"cypress-each": "1.14.0",
	"cypress-storybook": "1.0.0",
	eslint: "8.57.0",
	"eslint-plugin-react": "7.35.0",
	"focus-trap": "7.5.4",
	globby: "14.0.2",
	jest: "29.7.0",
	"jest-environment-jsdom": "29.7.0",
	rimraf: "6.0.1",
	"rollup-plugin-node-polyfills": "0.2.1",
	"rollup-plugin-scss": "4.0.0",
	"rollup-plugin-visualizer": "5.12.0",
	sass: "1.77.8",
	"start-server-and-test": "2.0.5",
	"ts-jest": "29.2.4",
	typescript: "5.5.4"
};
const keywords = [
	"components",
	"web-components",
	"header",
	"stencil",
	"design-system",
	"ui",
	"frontend"
];
const packageJson = {
	name: name,
	version: version,
	description: description,
	author: author,
	license: license,
	repository: repository,
	homepage: homepage,
	bugs: bugs,
	main: main,
	module: module,
	es2017: es2017,
	types: types,
	collection: collection,
	"collection:main": "dist/collection/index.js",
	unpkg: unpkg,
	publishConfig: publishConfig,
	files: files,
	scripts: scripts,
	dependencies: dependencies,
	devDependencies: devDependencies,
	keywords: keywords
};

const isScrollable = (node) => {
    if (!(node instanceof HTMLElement || node instanceof SVGElement)) {
        return false;
    }
    const style = getComputedStyle(node);
    return ['overflow', 'overflow-x', 'overflow-y'].some(propertyName => {
        const value = style.getPropertyValue(propertyName);
        return value === 'auto' || value === 'scroll';
    });
};
const getScrollParent = (node) => {
    let currentParent = node.parentElement;
    while (currentParent) {
        if (currentParent.nodeName === 'BODY') {
            return document;
        }
        if (isScrollable(currentParent)) {
            return currentParent;
        }
        currentParent = currentParent.parentElement;
    }
    return document;
};

/**
 * Returns intersection ratio
 */
const getLogoScale = (headerRef, metaHeaderRef) => {
    const scrollParent = getScrollParent(headerRef);
    const scrollDistance = scrollParent instanceof Element
        ? scrollParent.scrollTop
        : scrollParent.documentElement.scrollTop;
    const scrollY = state.stickyness === 'full' ? 0 : scrollDistance;
    let adjustedHeaderHeight, metaHeaderHeight;
    if (metaHeaderRef) {
        // Minus 1px border at the bottom that the logo is not covering
        adjustedHeaderHeight = headerRef.clientHeight - 1;
        metaHeaderHeight = metaHeaderRef.clientHeight;
    }
    else {
        const headerStyles = window.getComputedStyle(headerRef);
        const fontSize = parseFloat(headerStyles.getPropertyValue('font-size'));
        metaHeaderHeight = parseFloat(headerStyles.getPropertyValue('--meta-header-height')) * fontSize;
        adjustedHeaderHeight =
            metaHeaderHeight + parseFloat(headerStyles.getPropertyValue('--header-height')) * fontSize;
    }
    // If meta navigation is not visible (mobile, not configured), scale should just be 1
    let scale = 1;
    if (metaHeaderHeight > 0) {
        scale = Math.max((adjustedHeaderHeight - Math.max(scrollY, 0)) / (adjustedHeaderHeight - metaHeaderHeight), 1);
    }
    return scale.toString();
};

const registerLogoAnimationObserver = (target, headerRef) => {
    const scrollParent = getScrollParent(headerRef);
    /**
     * Set intersection ratio as CSS custom property
     */
    const handleScroll = () => {
        headerRef.style.setProperty('--logo-scale', getLogoScale(headerRef, target));
    };
    const debounced = debounce(150, handleScroll);
    /**
     * Observe the meta navigation in order to not track scroll events throughout the whole page.
     * This ensures that the scroll listener is only active while the meta navigation is visible.
     */
    const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            if (entry.isIntersecting && entry.intersectionRatio > 0) {
                scrollParent.addEventListener('scroll', handleScroll, { passive: true });
                window.addEventListener('resize', debounced);
                // Ensure callback is called at least once in case main thread is too busy while scrolling up
                window.requestAnimationFrame(handleScroll);
            }
            else {
                scrollParent.removeEventListener('scroll', handleScroll);
                window.removeEventListener('resize', debounced);
                window.requestAnimationFrame(handleScroll);
            }
        });
    }, {
        // Fires when element leaves viewport (0) and when it just about enters it (0.001)
        threshold: [0, 0.001],
    });
    observer.observe(target);
    return handleScroll;
};

const postInternetHeaderCss = ":host{--post-contrast-color:#000;--post-focus-color:#1976c8;--post-contrast-color-inverted:#fff;--post-gray-10:hsl(0, 0%, 90%);--post-gray-20:hsl(0, 0%, 80%);--post-gray-40:hsl(0, 0%, 60%);--post-gray-60:hsl(0, 0%, 40%);--post-gray-80:hsl(0, 0%, 20%);--post-yellow:#fc0;--post-white:#fff;--post-light:#faf9f8;--post-gray:#f4f3f1;--post-dark:hsl(0, 0%, 20%);--post-black:#000;--post-primary:hsl(0, 0%, 20%);--post-secondary:hsl(0, 0%, 40%);--post-success:#2c871d;--post-warning:#f49e00;--post-error:#a51728;--post-info:#cce4ee;--post-success-background:#c0debb;--post-warning-background:#fce2b2;--post-error-background:#ffdade;--post-nightblue:#004976;--post-nightblue-bright:#0076a8;--post-petrol:#006d68;--post-petrol-bright:#00968f;--post-coral:#9e2a2f;--post-coral-bright:#e03c31;--post-olive:#716135;--post-olive-bright:#aa9d2e;--post-purple:#80276c;--post-purple-bright:#c5299b;--post-aubergine:#523178;--post-aubergine-bright:#7566a0;--post-contrast-color-rgb:0, 0, 0;--post-contrast-color-inverted-rgb:255, 255, 255;--post-gray-10-rgb:230, 230, 230;--post-gray-20-rgb:204, 204, 204;--post-gray-40-rgb:153, 153, 153;--post-gray-60-rgb:102, 102, 102;--post-gray-80-rgb:51, 51, 51;--post-black-alpha-10-rgba:rgba(0, 0, 0, 0.1);--post-black-alpha-20-rgba:rgba(0, 0, 0, 0.2);--post-black-alpha-40-rgba:rgba(0, 0, 0, 0.4);--post-black-alpha-60-rgba:rgba(0, 0, 0, 0.6);--post-black-alpha-80-rgba:rgba(0, 0, 0, 0.8);--post-white-alpha-10-rgba:rgba(255, 255, 255, 0.1);--post-white-alpha-20-rgba:rgba(255, 255, 255, 0.2);--post-white-alpha-40-rgba:rgba(255, 255, 255, 0.4);--post-white-alpha-60-rgba:rgba(255, 255, 255, 0.6);--post-white-alpha-80-rgba:rgba(255, 255, 255, 0.8);--post-yellow-rgb:255, 204, 0;--post-white-rgb:255, 255, 255;--post-light-rgb:250, 249, 248;--post-gray-rgb:244, 243, 241;--post-dark-rgb:51, 51, 51;--post-black-rgb:0, 0, 0;--post-primary-rgb:51, 51, 51;--post-secondary-rgb:102, 102, 102;--post-success-rgb:44, 135, 29;--post-warning-rgb:244, 158, 0;--post-error-rgb:165, 23, 40;--post-info-rgb:204, 228, 238;--post-success-background-rgb:192, 222, 187;--post-warning-background-rgb:252, 226, 178;--post-error-background-rgb:255, 218, 222;--post-nightblue-rgb:0, 73, 118;--post-nightblue-bright-rgb:0, 118, 168;--post-petrol-rgb:0, 109, 104;--post-petrol-bright-rgb:0, 150, 143;--post-coral-rgb:158, 42, 47;--post-coral-bright-rgb:224, 60, 49;--post-olive-rgb:113, 97, 53;--post-olive-bright-rgb:170, 157, 46;--post-purple-rgb:128, 39, 108;--post-purple-bright-rgb:197, 41, 155;--post-aubergine-rgb:82, 49, 120;--post-aubergine-bright-rgb:117, 102, 160}@font-face{font-display:swap;font-family:\"Swiss Post Sans\";font-weight:700;src:url(\"https://fonts.post.ch/swiss-post-sans/v1/SwissPostSans-Bold.woff2\") format(\"woff2\"),url(\"https://fonts.post.ch/swiss-post-sans/v1/SwissPostSans-Bold.woff\") format(\"woff\")}@font-face{font-display:swap;font-family:\"Swiss Post Sans\";font-weight:400;src:url(\"https://fonts.post.ch/swiss-post-sans/v1/SwissPostSans-Regular.woff2\") format(\"woff2\"),url(\"https://fonts.post.ch/swiss-post-sans/v1/SwissPostSans-Regular.woff\") format(\"woff\")}@font-face{font-display:swap;font-family:\"Swiss Post Sans\";font-weight:300;src:url(\"https://fonts.post.ch/swiss-post-sans/v1/SwissPostSans-Light.woff2\") format(\"woff2\"),url(\"https://fonts.post.ch/swiss-post-sans/v1/SwissPostSans-Light.woff\") format(\"woff\")}.visually-hidden{position:absolute;width:1px;height:1px;border:0;padding:0;margin:-1px;overflow:hidden;clip:rect(0 0 0 0)}.btn-blank{background-color:rgba(0,0,0,0);border:none;border-radius:0;padding:0}.nav-link{outline:none;outline-offset:2px;text-decoration:none;color:rgba(0,0,0,.8);-webkit-transition:color 200ms;transition:color 200ms;background-color:rgba(0,0,0,0);-webkit-box-shadow:none;box-shadow:none;border:0;margin:0;cursor:pointer;display:-ms-flexbox;display:flex;-ms-flex-align:center;align-items:center;gap:.5rem}.nav-link:is(:focus-visible,:focus-within,.pretend-focus){outline:2px solid var(--post-contrast-color)}.nav-link:hover,.nav-link:focus{color:#000}.nav-link:is(:focus-visible,:focus-within,.pretend-focus){z-index:1;position:relative}.nav-link>svg{width:1.4em;height:1.4em;-ms-flex-negative:0;flex-shrink:0}.nav-link>span{-ms-flex-negative:1;flex-shrink:1}.no-list{list-style:none;padding-left:0;margin-top:0;margin-bottom:0}*,:host,*::before,*::after{-webkit-box-sizing:border-box;box-sizing:border-box}:host{font-family:\"Swiss Post Sans\",-apple-system,BlinkMacSystemFont,\"Segoe UI\",Roboto,\"Helvetica Neue\",Arial,sans-serif}button{font:inherit;padding:0}a,button{border-radius:4px;outline:none;outline-offset:2px}a:is(:focus-visible,:focus-within,.pretend-focus),button:is(:focus-visible,:focus-within,.pretend-focus){outline:2px solid var(--post-contrast-color);border-radius:4px}img,svg{max-width:100%;max-height:100%}@media (forced-colors: active),(-ms-high-contrast: active),(-ms-high-contrast: white-on-black){svg{color:currentColor;forced-color-adjust:preserve-parent-color}}.box>*:first-child{margin-top:0}.box>*:last-child{margin-bottom:0}.mirrored{-webkit-transform:scaleX(-1);transform:scaleX(-1)}.rotate-270{-webkit-transform:rotate(270deg);transform:rotate(270deg)}.bold{font-weight:700}.light{font-weight:300}.d-none{display:none !important}.d-flex{display:-ms-flexbox;display:flex}.d-inline-flex{display:-ms-inline-flexbox;display:inline-flex}.align-items-center{-ms-flex-align:center;align-items:center}@media (min-width: 1441px){.wide-container{margin:0 auto;max-width:1440px}}@media (max-width: 599.98px){.container{padding-right:16px;padding-left:16px}}@media (min-width: 600px)and (max-width: 1023.98px){.container{padding-right:32px;padding-left:32px}}@media (min-width: 1024px){.container{padding-right:40px;padding-left:40px}}@media (max-width: -0.02px){.hidden-xs{display:none}}@media (max-width: 399.98px){.hidden-sm{display:none}}@media (max-width: 599.98px){.hidden-rg{display:none}}@media (max-width: 779.98px){.hidden-md{display:none}}@media (max-width: 1023.98px){.hidden-lg{display:none}}@media (max-width: 1279.98px){.hidden-xl{display:none}}@media (max-width: 1439.98px){.hidden-xxl{display:none}}:host{--logo-scale:1}@media (max-width: 1023.98px){:host{--logo-scale:1 !important}}post-header-logo{height:var(--header-height);width:var(--header-height);-webkit-transform-origin:bottom left;transform-origin:bottom left;-webkit-transform:scale(var(--logo-scale));transform:scale(var(--logo-scale))}post-main-navigation{margin-left:calc(var(--header-height)*var(--logo-scale) - var(--header-height))}:host{display:block;position:relative;font-size:1rem;font-weight:300;z-index:var(--header-z-index, 10);--language-dropdown-margin-top:0px;--meta-header-height:var(--post-meta-header-height, 0px);--header-height:var(--post-header-height, 3.5rem)}@media (min-width: 1024px){:host{--meta-header-height:var(--post-meta-header-height, 3rem)}}@media (min-width: 1024px){:host{--header-height:var(--post-header-height, 4rem)}}@media (min-width: 1280px){:host{--header-height:var(--post-header-height, 4.5rem)}}:host([meta=false]){--meta-header-height:0px;--language-dropdown-margin-top:1px}@supports ((position: -webkit-sticky) or (position: sticky)){:host(:not(.stickyness-none)){position:-webkit-sticky;position:sticky}:host(.stickyness-full){top:0}:host(.stickyness-main){top:calc(var(--meta-header-height, 0px)*-1)}:host(.stickyness-minimal){top:calc((var(--header-height, 0px) + var(--meta-header-height, 0px) + 1px)*-1);-webkit-transition:top 200ms ease-in;transition:top 200ms ease-in}:host(.stickyness-minimal.scrolling-up),:host(.stickyness-minimal.dropdown-open){top:calc(var(--meta-header-height, 0px)*-1);--header-slide-in-transition:var(--post-header-slide-in-transition, top 200ms ease-out);-webkit-transition:var(--header-slide-in-transition);transition:var(--header-slide-in-transition)}}.post-internet-header{border-bottom:1px solid #e6e6e6;background-color:#fff}.main-navigation-container{display:-ms-flexbox;display:flex}.main-navigation-container>.main-navigation-controls{-ms-flex:0 1 auto;flex:0 1 auto}.main-navigation{display:-ms-flexbox;display:flex}.sub-navigation-container{position:absolute;top:100%;left:0;width:100%;display:-ms-flexbox;display:flex;background:#fff;visibility:hidden}.main-navigation-item:hover .sub-navigation-container{visibility:visible}.main-navigation-controls{display:-ms-flexbox;display:flex;margin-left:auto}@media (max-width: 1023.98px){.main-navigation-controls{font-weight:400}}@media (min-width: 1024px){.main-navigation-controls{font-size:1.0625rem}}.main-navigation-controls>*{border-left:1px solid #e6e6e6;-ms-flex:0 0 auto;flex:0 0 auto}.main-navigation-custom-content{max-height:var(--header-height);display:-ms-flexbox;display:flex;-ms-flex-align:center;align-items:center;-ms-flex-negative:1;flex-shrink:1}.menu-button{display:-ms-flexbox;display:flex;-ms-flex-align:center;align-items:center;padding:0 calc(1rem - 1px);font-weight:300;cursor:pointer;border-left:1px solid #e6e6e6;-ms-flex-order:1;order:1}@media (min-width: 1024px){.menu-button{display:none}}@media (min-width: 600px)and (max-width: 1023.98px){.menu-button .visually-hidden{position:static;width:auto;height:auto;margin:auto;overflow:visible;clip:auto;visibility:visible;margin-right:.5rem}}.menu-button svg{width:1.5rem;height:1.5rem}.full-width .wide-container{max-width:none}";
const SwisspostInternetHeaderStyle0 = postInternetHeaderCss;

const PostInternetHeader = class {
    /**
     * Get the currently set language as a two letter string ("de", "fr" "it" or "en")
     * @returns string
     */
    async getCurrentLanguage() {
        var _a;
        return (_a = state.currentLanguage) !== null && _a !== void 0 ? _a : 'de';
    }
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.headerLoaded = createEvent(this, "headerLoaded", 7);
        this.lastScrollTop = window.scrollY || document.documentElement.scrollTop;
        this.lastWindowWidth = window.innerWidth;
        this.handleClickOutsideBound = this.handleClickOutside.bind(this);
        this.project = undefined;
        this.stickyness = 'minimal';
        this.language = undefined;
        this.meta = true;
        this.login = true;
        this.search = true;
        this.skiplinks = true;
        this.configProxy = undefined;
        this.environment = 'prod';
        this.languageSwitchOverrides = undefined;
        this.customConfig = undefined;
        this.languageCookieKey = undefined;
        this.languageLocalStorageKey = 'swisspost-internet-header-language';
        this.logoutUrl = undefined;
        this.activeRoute = 'auto';
        this.osFlyoutOverrides = undefined;
        this.fullWidth = false;
        this.activeFlyout = null;
        this.activeDropdownElement = null;
        this.isMainSlotEmpty = true;
        if (this.project === undefined || this.project === '' || !isValidProjectId(this.project)) {
            throw new Error(`Internet Header project key is "${this.project}". Please provide a valid project key.`);
        }
    }
    connectedCallback() {
        this.throttledScroll = throttle(300, () => this.handleScrollEvent());
        this.debouncedResize = debounce(200, () => this.handleResize());
    }
    disconnectedCallback() {
        this.scrollParent.removeEventListener('scroll', this.throttledScroll);
        this.scrollParent.removeEventListener('resize', this.debouncedResize);
        // Reset the store to its original state
        dispose();
    }
    async componentWillLoad() {
        this.scrollParent = getScrollParent(this.host);
        this.scrollParent.addEventListener('scroll', this.throttledScroll, { passive: true });
        this.scrollParent.addEventListener('resize', this.debouncedResize, { passive: true });
        // Wait for the config to arrive, then render the header
        try {
            state.projectId = this.project;
            state.stickyness = this.stickyness;
            state.environment = this.environment.toLocaleLowerCase();
            if (this.language !== undefined)
                state.currentLanguage = this.language;
            state.languageSwitchOverrides =
                typeof this.languageSwitchOverrides === 'string'
                    ? JSON.parse(this.languageSwitchOverrides)
                    : this.languageSwitchOverrides;
            state.osFlyoutOverrides =
                typeof this.osFlyoutOverrides === 'string'
                    ? JSON.parse(this.osFlyoutOverrides)
                    : this.osFlyoutOverrides;
            if (this.customConfig !== undefined) {
                const langs = Object.keys(typeof this.customConfig === 'string' ? JSON.parse(this.customConfig) : this.customConfig);
                const lang = state.currentLanguage || getUserLang(langs, this.language);
                state.localizedCustomConfig = getLocalizedCustomConfig(this.customConfig, lang);
            }
            state.localizedConfig = await getLocalizedConfig({
                projectId: this.project,
                environment: state.environment,
                language: this.language,
                cookieKey: this.languageCookieKey,
                localStorageKey: this.languageLocalStorageKey,
                activeRouteProp: this.activeRoute,
                localizedCustomConfig: state.localizedCustomConfig,
                osFlyoutOverrides: state.osFlyoutOverrides,
            });
        }
        catch (error) {
            console.error(error);
        }
    }
    componentDidLoad() {
        window.requestAnimationFrame(() => {
            this.handleResize();
            this.headerLoaded.emit();
            this.host.classList.add('header-loaded');
            if (this.meta && this.metaNav) {
                this.updateLogoAnimation = registerLogoAnimationObserver(this.metaNav, this.host);
            }
            else {
                // Set height to 0 if meta is never visible and global variables are defined
                const rootStyles = window.getComputedStyle(document.documentElement);
                if (rootStyles.getPropertyValue('--post-meta-header-height') !== '') {
                    document.documentElement.style.setProperty('--post-meta-header-height', '0px');
                }
            }
        });
        if (this.stickyness === 'full')
            console.warn('Internet Header: The stickyness="full" option is deprecated.');
    }
    async handleLanguageChange(newValue) {
        state.currentLanguage = newValue;
        state.localizedConfig = await getLocalizedConfig({
            projectId: this.project,
            environment: this.environment,
            language: newValue,
            cookieKey: this.languageCookieKey,
            localStorageKey: this.languageLocalStorageKey,
            activeRouteProp: this.activeRoute,
            localizedCustomConfig: state.localizedCustomConfig,
            osFlyoutOverrides: state.osFlyoutOverrides,
        });
        if (this.customConfig !== undefined)
            state.localizedCustomConfig = getLocalizedCustomConfig(this.customConfig, newValue);
    }
    handleAvailableLanguagesChange(newValue) {
        state.languageSwitchOverrides = typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
    }
    async handleOsFlyoutOverrides(newValue) {
        state.osFlyoutOverrides = typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
        state.localizedConfig = await getLocalizedConfig({
            projectId: this.project,
            environment: this.environment,
            language: this.language,
            cookieKey: this.languageCookieKey,
            localStorageKey: this.languageLocalStorageKey,
            activeRouteProp: this.activeRoute,
            localizedCustomConfig: state.localizedCustomConfig,
            osFlyoutOverrides: state.osFlyoutOverrides,
        });
    }
    async handleActiveRouteChange(newValue) {
        state.localizedConfig = await getLocalizedConfig({
            projectId: this.project,
            environment: this.environment,
            language: this.language,
            cookieKey: this.languageCookieKey,
            localStorageKey: this.languageLocalStorageKey,
            activeRouteProp: newValue,
            localizedCustomConfig: state.localizedCustomConfig,
            osFlyoutOverrides: state.osFlyoutOverrides,
        });
    }
    async handleCustomConfigChange(newValue) {
        if (this.language === undefined)
            return;
        const localizedCustomConfig = getLocalizedCustomConfig(newValue, this.language);
        state.localizedCustomConfig = localizedCustomConfig;
        state.localizedConfig = await getLocalizedConfig({
            projectId: this.project,
            environment: this.environment,
            language: this.language,
            cookieKey: this.languageCookieKey,
            localStorageKey: this.languageLocalStorageKey,
            activeRouteProp: this.activeRoute,
            localizedCustomConfig: localizedCustomConfig,
            osFlyoutOverrides: state.osFlyoutOverrides,
        });
    }
    handleSearchChange(newValue) {
        state.search = newValue;
    }
    handleLoginChange(newValue) {
        state.login = newValue;
    }
    handleMetaChange(newValue) {
        state.meta = newValue;
    }
    handleLanguageChangeEvent(event) {
        void this.handleLanguageChange(event.detail);
    }
    handleStickynessChange(newValue) {
        state.stickyness = newValue;
        if (typeof this.updateLogoAnimation === 'function')
            this.updateLogoAnimation();
    }
    handleClickOutside(event) {
        // Close active dropdown element on click outside of it
        if (this.activeDropdownElement && !event.composedPath().includes(this.activeDropdownElement)) {
            void this.activeDropdownElement.toggleDropdown(false);
        }
    }
    handleKeyUp(event) {
        if (event.key === 'Escape') {
            if (this.activeDropdownElement) {
                void this.activeDropdownElement.toggleDropdown(false);
            }
            if (this.activeFlyout !== null && this.mainNav) {
                void this.mainNav.toggleDropdown(false);
            }
        }
    }
    handleScrollEvent() {
        // Credits: "https://github.com/qeremy/so/blob/master/so.dom.js#L426"
        const st = this.scrollParent instanceof Document
            ? this.scrollParent.documentElement.scrollTop
            : this.scrollParent.scrollTop;
        // Toggle class without re-rendering the component if stickyness is minimal
        // the other stickyness modes do not need the class
        if (this.stickyness === 'minimal') {
            this.host.classList.toggle('scrolling-up', st <= this.lastScrollTop);
        }
        // For Mobile or negative scrolling
        this.lastScrollTop = st <= 0 ? 0 : st;
    }
    handleResize() {
        var _a;
        // Close main navigation dropdown if size changed to bigger than 1024px (search dropdown will be kept open)
        if (this.isMainNavOpen() && this.lastWindowWidth < 1024 && window.innerWidth >= 1024) {
            (_a = this.activeDropdownElement) === null || _a === void 0 ? void 0 : _a.toggleDropdown(false);
        }
        this.lastWindowWidth = window.innerWidth;
    }
    /**
     * Close open dropdown menus if another is being opened
     *
     * @param event Dropdown toggled event
     * @returns void
     */
    handleDropdownToggled(event) {
        if (!event.detail.open && this.activeDropdownElement !== event.detail.element) {
            // Some dropdown got closed programmatically (possibly by this function). To prevent
            // a circle or an outdated state, we'll not handle this event
            return;
        }
        if (event.detail.open) {
            if (this.activeDropdownElement) {
                void this.activeDropdownElement.toggleDropdown(false);
            }
            this.activeDropdownElement = event.detail.element;
            if (window.innerWidth >= 1024) {
                // Add event listener to close active dropdown element on click outside of it
                // Also adds 10ms delay in case of an external interaction:
                //    Some button on the page calls toggleDropdown() -> dropdown opens
                //    Click event bubbles to the window, this.handleClickOutsideBound closes dropdown again
                window.setTimeout(() => {
                    window.addEventListener('click', this.handleClickOutsideBound);
                }, 10);
            }
            if (this.activeFlyout !== null && this.mainNav) {
                void this.mainNav.setActiveFlyout(null);
            }
        }
        else {
            this.activeDropdownElement = null;
            // Remove event listener as it is not needed if no dropdown element is active
            window.removeEventListener('click', this.handleClickOutsideBound);
        }
    }
    /**
     * Close open dropdowns if the flyout is being opened
     * @param event Flyout toggle event
     */
    handleFlyoutToggled(event) {
        this.activeFlyout = event.detail;
        if (this.activeDropdownElement && event.detail !== null && !this.isMainNavOpen()) {
            void this.activeDropdownElement.toggleDropdown(false);
        }
    }
    toggleMobileDropdown() {
        var _a;
        (_a = this.mainNav) === null || _a === void 0 ? void 0 : _a.toggleDropdown();
    }
    isMainNavOpen() {
        return (this.activeDropdownElement && this.activeDropdownElement.tagName === 'POST-MAIN-NAVIGATION');
    }
    handleMainSlotChange(e) {
        const mainSlot = e.target;
        this.isMainSlotEmpty = mainSlot.assignedElements().length === 0;
    }
    render() {
        var _a, _b, _c;
        if (!((_a = state.localizedConfig) === null || _a === void 0 ? void 0 : _a.header)) {
            console.error(new Error('Internet Header: Config cannot be loaded'));
            return;
        }
        const config = state.localizedConfig;
        const renderMetaNavigation = this.meta &&
            config.header.navMeta !== undefined &&
            ((_b = config.header.navMeta) === null || _b === void 0 ? void 0 : _b.filter(meta => !meta.isHomeLink).length) > 0;
        const renderLogin = ((_c = this.login) !== null && _c !== void 0 ? _c : !config.header.isLoginWidgetHidden) && config.header.loginWidgetOptions;
        const renderLanguageSwitch = config.header.navLang.length > 1;
        const initialLogoScale = renderMetaNavigation ? getLogoScale(this.host) : '1';
        return (h(Host, { class: `stickyness-${this.stickyness} ${Boolean(this.activeDropdownElement) || Boolean(this.activeFlyout) ? 'dropdown-open' : ''}`, "data-version": packageJson.version, onKeyup: (e) => this.handleKeyUp(e), style: { '--logo-scale': initialLogoScale } }, h("header", { class: `post-internet-header${this.fullWidth ? ' full-width' : ''}` }, h(SvgSprite, null), h("h1", { class: "visually-hidden" }, translate('Navigate on post.ch')), h(If, { condition: this.skiplinks }, h("post-skiplinks", null)), h(If, { condition: renderMetaNavigation }, h("post-meta-navigation", { orientation: "horizontal", class: "hidden-lg", "full-width": this.fullWidth, ref: el => (this.metaNav = el) }, h(If, { condition: renderLanguageSwitch }, h("post-language-switch", { id: "post-language-switch-desktop", mode: "dropdown", onDropdownToggled: e => this.handleDropdownToggled(e) })))), h("div", { class: "main-navigation-container wide-container" }, h("post-header-logo", null), h("button", { class: "menu-button nav-link", onClick: () => this.toggleMobileDropdown(), "aria-haspopup": "menu", "aria-controls": "post-internet-header-main-navigation", "aria-expanded": this.activeDropdownElement ? 'true' : 'false' }, h("span", { class: "menu-button-text visually-hidden" }, config.header.mobileMenu.text), h(SvgIcon, { name: this.isMainNavOpen() ? 'pi-close' : 'pi-menu' })), h("post-main-navigation", { onDropdownToggled: e => this.handleDropdownToggled(e), onFlyoutToggled: e => this.handleFlyoutToggled(e), ref: el => (this.mainNav = el) }, h(If, { condition: renderMetaNavigation }, h("post-meta-navigation", { orientation: "vertical" }, h(If, { condition: renderLanguageSwitch }, h("post-language-switch", { id: "post-language-switch-mobile", mode: "list" }))))), h("div", { class: "main-navigation-controls" }, h("div", { class: `main-navigation-custom-content${this.isMainSlotEmpty ? ' d-none' : ''}` }, h("slot", { name: "main", onSlotchange: e => this.handleMainSlotChange(e) })), h(If, { condition: this.search }, h("post-search", { onDropdownToggled: e => this.handleDropdownToggled(e) })), h(If, { condition: !!renderLogin }, h("post-klp-login-widget", { "logout-url": this.logoutUrl }, h("slot", { name: "login-widget" }))), h(If, { condition: !renderMetaNavigation && renderLanguageSwitch }, h("post-language-switch", { id: "post-language-switch-no-meta", onDropdownToggled: e => this.handleDropdownToggled(e), mode: "dropdown" })))))));
    }
    get host() { return getElement(this); }
    static get watchers() { return {
        "language": ["handleLanguageChange"],
        "languageSwitchOverrides": ["handleAvailableLanguagesChange"],
        "osFlyoutOverrides": ["handleOsFlyoutOverrides"],
        "activeRoute": ["handleActiveRouteChange"],
        "customConfig": ["handleCustomConfigChange"],
        "search": ["handleSearchChange"],
        "login": ["handleLoginChange"],
        "meta": ["handleMetaChange"],
        "stickyness": ["handleStickynessChange"]
    }; }
};
PostInternetHeader.style = SwisspostInternetHeaderStyle0;

export { PostInternetHeader as P };
